import { api } from '@/shared/lib/api';
import { useAuthStore } from '@/features/auth/stores/auth-store';
import { markCustomerAgentDisabled } from '@/features/whatsapp/hooks/useCustomerAgentAvailability';
import { WHATSAPP_ERRORS } from '@/features/whatsapp/lib/whatsapp-errors';
import { markConversationsForbidden } from '../hooks/useConversationsAccess';
import { apiErrorCode, apiErrorStatus } from '../lib/conversation-errors';
import type {
  ContactDoNotServeResponse,
  ConversationAssumeResponse,
  ConversationByOrder,
  ConversationDetail,
  ConversationHumanOnlyResponse,
  ConversationListItem,
  ConversationMessage,
  ConversationReplyRequest,
  ConversationReplyResponse,
  ConversationReturnResponse,
  ConversationSummary,
  ConversationView,
  CustomerAgentStatus,
  PagedResponse,
} from '../types/conversations.types';

/* ==========================================================================
   Client do Atendimento (`/api/v1/conversations`, design D11 a D14) e das
   rotas de contato do canal que a conversa usa ("Não atender" e "Apagar
   dados deste contato", `/api/v1/whatsapp/contacts/{contactId}`).

   Toda chamada passa pelo `gate`: o 404 `whatsapp.disabled` marca a loja
   como fora da área do Atendente IA (a tela, a faixa e o item somem na
   hora), e o 403 de uma leitura esconde as peças em silêncio até a próxima
   renovação do token (claims novas só chegam no token seguinte).
   ========================================================================== */

const CONVERSATIONS = '/conversations';

/** O cabeçalho da chave de idempotência da resposta pelo painel. */
export const IDEMPOTENCY_KEY_HEADER = 'Idempotency-Key';

/** O painel pede as mensagens em páginas de 50 (o padrão da API é 20, o máximo 100). */
export const MESSAGES_PAGE_SIZE = 50;

/** A lista mostra até 50 conversas por aba. */
export const LIST_PAGE_SIZE = 50;

function tenantId(): string {
  return useAuthStore.getState().selectedTenant?.id ?? '';
}

async function gate<T>(request: Promise<T>, { read }: { read: boolean }): Promise<T> {
  try {
    return await request;
  } catch (error) {
    if (apiErrorCode(error) === WHATSAPP_ERRORS.disabled) {
      markCustomerAgentDisabled(tenantId());
    } else if (read && apiErrorStatus(error) === 403) {
      markConversationsForbidden();
    }
    throw error;
  }
}

async function read<T>(request: Promise<{ data: T }>): Promise<T> {
  const response = await gate(request, { read: true });
  return response.data;
}

async function act<T>(request: Promise<{ data: T }>): Promise<T> {
  const response = await gate(request, { read: false });
  return response.data;
}

const conversationPath = (conversationId: string) => `${CONVERSATIONS}/${encodeURIComponent(conversationId)}`;

/* ---- Leituras (`conversations.read`) ---- */

export const getConversations = (view: ConversationView, page = 1, pageSize = LIST_PAGE_SIZE) =>
  read(api.get<PagedResponse<ConversationListItem>>(CONVERSATIONS, { params: { view, page, pageSize } }));

export const getConversationsSummary = () => read(api.get<ConversationSummary>(`${CONVERSATIONS}/summary`));

export const getConversation = (conversationId: string) =>
  read(api.get<ConversationDetail>(conversationPath(conversationId)));

/** Da mais nova para a mais antiga; o painel inverte para mostrar da mais antiga para a mais nova. */
export const getConversationMessages = (conversationId: string, page = 1, pageSize = MESSAGES_PAGE_SIZE) =>
  read(
    api.get<PagedResponse<ConversationMessage>>(`${conversationPath(conversationId)}/messages`, {
      params: { page, pageSize },
    }),
  );

export const getConversationByOrder = (orderId: string) =>
  read(api.get<ConversationByOrder>(`${CONVERSATIONS}/by-order/${encodeURIComponent(orderId)}`));

/**
 * A mídia do cliente, buscada só no clique, pelo client autenticado (o token
 * vai no cabeçalho, nunca na URL). Devolve o `Blob`; quem chama monta e
 * revoga o `blob:`.
 */
export const getConversationMessageMedia = (conversationId: string, messageId: string) =>
  read(
    api.get<Blob>(`${conversationPath(conversationId)}/messages/${encodeURIComponent(messageId)}/media`, {
      responseType: 'blob',
      timeout: 60_000,
    }),
  );

/** `GET /customer-agent/status` (`settings.read` ou `conversations.read`). */
export const getCustomerAgentStatus = () => read(api.get<CustomerAgentStatus>('/customer-agent/status'));

/* ---- Ações (`conversations.reply`) ---- */

export const assumeConversation = (conversationId: string) =>
  act(api.post<ConversationAssumeResponse>(`${conversationPath(conversationId)}/assume`));

export const replyToConversation = (conversationId: string, request: ConversationReplyRequest, idempotencyKey: string) =>
  act(
    api.post<ConversationReplyResponse>(`${conversationPath(conversationId)}/messages`, request, {
      headers: { [IDEMPOTENCY_KEY_HEADER]: idempotencyKey },
    }),
  );

export const returnConversationToAi = (conversationId: string) =>
  act(api.post<ConversationReturnResponse>(`${conversationPath(conversationId)}/return-to-ai`));

export const setConversationHumanOnly = (conversationId: string, humanOnly: boolean) =>
  act(api.put<ConversationHumanOnlyResponse>(`${conversationPath(conversationId)}/human-only`, { humanOnly }));

/* ---- Contato, pelas rotas do canal ---- */

/** `PUT /whatsapp/contacts/{contactId}/do-not-serve` (`conversations.reply`). */
export const setContactDoNotServe = (contactId: string, doNotServe: boolean) =>
  act(
    api.put<ContactDoNotServeResponse>(`/whatsapp/contacts/${encodeURIComponent(contactId)}/do-not-serve`, {
      doNotServe,
    }),
  );

/** `DELETE /whatsapp/contacts/{contactId}` (`whatsapp.manage`; 204). */
export const eraseContactData = (contactId: string) =>
  gate(api.delete(`/whatsapp/contacts/${encodeURIComponent(contactId)}`), { read: false }).then(() => undefined);
